import { AbilityBuilder, PureAbility } from '@casl/ability';
import type { User, UserRole, Permission } from '../../types/Auth';
import { ROLE_DEFINITIONS } from '../../constants/roles';

// Define our application's ability type using simple action/subject pairs
export type AppAbility = PureAbility<[string, string]>;

/**
 * Maps our permission strings to CASL actions and subjects
 */
export const mapPermissionToAbility = (permission: Permission): { action: string; subject: string } => {
  const [subject, action] = permission.split(':') as [string, string];
  
  const subjectMap: Record<string, string> = {
    users: 'User',
    organizations: 'Organization',
    products: 'Product',
    quotes: 'Quote',
    policies: 'Policy',
    claims: 'Claim',
    payments: 'Payment',
    farmers: 'Farmer',
    system: 'System',
    reports: 'Report',
    ledger: 'Ledger',
    notifications: 'Notification',
    roles: 'Role',
    permissions: 'Permission',
    // Navigation permissions - keep as-is (lowercase)
    topnav: 'topnav',
    nav: 'nav',
  };

  return {
    action: action,
    subject: subjectMap[subject] || subject,
  };
};

/**
 * Defines abilities for a user based on their permissions from backend
 */
export function defineAbilitiesFor(user: User | null): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(PureAbility);

  if (!user || !user.roles?.length) {
    // No permissions for unauthenticated or users without roles
    return build();
  }

  // Use permissions from backend if available, otherwise fall back to role definitions
  let allPermissions: Set<Permission>;
  
  if (user.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
    // Use permissions from backend (preferred - database is source of truth)
    allPermissions = new Set<Permission>(user.permissions as Permission[]);
  } else {
    // Fallback to frontend role definitions if backend doesn't send permissions
    allPermissions = new Set<Permission>();
    user.roles.forEach(roleName => {
      const roleDefinition = ROLE_DEFINITIONS[roleName as UserRole];
      if (roleDefinition) {
        roleDefinition.permissions.forEach(permission => allPermissions.add(permission));
      }
    });
  }

  // Convert permissions to CASL abilities
  allPermissions.forEach(permission => {
    const { action, subject } = mapPermissionToAbility(permission);
    can(action, subject);
  });

  // Special rules for admins - they can do everything
  if (user.roles.includes('admin')) {
    can('manage', 'all');
  }

  // Add resource-level permissions
  // Users can only update their own profile
  if (user.roles.includes('user')) {
    can('update', 'OwnProfile');
  }

  // Reinsurers can only access claims from their organization
  if (user.roles.includes('reinsurer') && user.organization) {
    can('read', 'OrganizationClaims');
    can('update', 'OrganizationClaims');
    can('approve', 'OrganizationClaims');
  }

  // System operators cannot delete admin users
  if (user.roles.includes('system_operator')) {
    cannot('delete', 'AdminUser');
  }

  return build();
}

/**
 * Creates an ability instance for the current user
 */
export function createAppAbility(user: User | null): AppAbility {
  return defineAbilitiesFor(user);
}

/**
 * Helper function to check if user can perform an action
 */
export function checkAbility(
  ability: AppAbility, 
  action: string, 
  subject: string
): boolean {
  return ability.can(action, subject);
}

/**
 * Helper function to get readable error message for denied abilities
 */
export function getAbilityErrorMessage(
  action: string, 
  subject: string
): string {
  return `You don't have permission to ${action} ${subject.toLowerCase()}`;
}

/**
 * Helper to convert our permission format to CASL check
 */
export function canUserAccess(
  ability: AppAbility,
  permission: Permission
): boolean {
  const { action, subject } = mapPermissionToAbility(permission);
  return checkAbility(ability, action, subject);
}

/**
 * Helper to check if user can access a specific resource
 */
export function canAccessResource(
  ability: AppAbility,
  action: string,
  subject: string
): boolean {
  return ability.can(action, subject);
} 